import React, { useEffect, useMemo, useState } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { InsertDriveFile } from '@mui/icons-material';
import { BaseDialog } from '../common/BaseDialog';
import { chatService } from '../../services/chat.service';
import { toastService } from '../../services/toast.service';
import { getErrorMessage } from '../../utils/api-response.util';
import { ConversationFile } from '../../types/chat.types';

interface ConversationFilesDialogProps {
  open: boolean;
  onClose: () => void;
  conversationId: number;
}

export const ConversationFilesDialog: React.FC<ConversationFilesDialogProps> = ({
  open,
  onClose,
  conversationId,
}) => {
  const [files, setFiles] = useState<ConversationFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setLoading(true);
    chatService
      .getConversationFiles(conversationId)
      .then(setFiles)
      .catch((error) => {
        toastService.error(getErrorMessage(error, 'Не вдалося завантажити файли розмови'));
      })
      .finally(() => setLoading(false));
  }, [open, conversationId]);

  const filteredFiles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) => f.fileName.toLowerCase().includes(q));
  }, [files, query]);

  return (
    <BaseDialog open={open} onClose={onClose} title="Файли розмови" maxWidth="sm">
      <TextField
        fullWidth
        placeholder="Пошук за назвою файлу"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 2 }}
      />

      {loading && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && filteredFiles.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
          {files.length === 0 ? 'У цій розмові ще немає файлів' : 'Нічого не знайдено'}
        </Typography>
      )}

      {!loading && filteredFiles.length > 0 && (
        <List disablePadding>
          {filteredFiles.map((file) => (
            <ListItem key={file.messageId} disablePadding>
              <ListItemButton
                component="a"
                href={file.url}
                download={file.fileName}
                target="_blank"
                rel="noopener"
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon>
                  <Avatar variant="rounded">
                    <InsertDriveFile />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={file.fileName}
                  secondary={
                    file.senderName +
                    (file.sentAt ? ` · ${new Date(file.sentAt).toLocaleDateString('uk-UA')}` : '')
                  }
                  primaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </BaseDialog>
  );
};
